import React, { useState, useCallback } from 'react';
import { Box, Chip, Typography, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { GridRenderCellParams, GridRowId } from '@mui/x-data-grid';

import PaginatedDataGrid, {
  PaginatedDataGridColumn,
  PaginatedDataGridAction
} from 'src/components/paginated-data-grid/PaginatedDataGrid';

import SimpleConfirmationDialog from 'src/components/SimpleConfirmationDialog';
import CurrencyDialog from './CurrencyDialog';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface Currency {
  id: string;
  name: string;
  description: string;
  code: string;
  exchangeRate: number;
  isDefault: boolean;
  active: boolean;
}

const CurrenciesListing: React.FC = () => {
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(null);
  const [toggleLoading, setToggleLoading] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  // ---------- Delete Currency ----------
  const handleDeleteClick = (id: GridRowId) => {
    setDeleteId(String(id));
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;

    try {
      const response = await DataService.delete(`api/Currency/${deleteId}`, {});
      if (response.ok) {
        setRefreshKey((prev) => prev + 1);
        dispatch(
          showAlert({
            message: 'Currency deleted successfully!',
            type: 'success'
          })
        );
      } else {
        throw new Error('Failed to delete currency');
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to delete currency. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteId(null);
  };

  // ---------- Edit Currency ----------
  const handleEditClick = (currency: Currency) => {
    setSelectedCurrency(currency);
    setEditDialogOpen(true);
  };

  // ---------- Add Currency ----------
  const handleAddCurrency = () => {
    setSelectedCurrency(null);
    setEditDialogOpen(true);
  };

  // ---------- Toggle Active/Inactive ----------
  const handleToggleActive = async (currency: Currency) => {
    setToggleLoading(currency.id);

    try {
      const endpoint = currency.active
        ? `api/Currency/deactivate?id=${currency.id}`
        : `api/Currency/activate?id=${currency.id}`;

      const response = await DataService.patch(endpoint, {});

      if (response.ok) {
        setRefreshKey((prev) => prev + 1);
        dispatch(
          showAlert({
            message: `Currency ${currency.active ? 'deactivated' : 'activated'} successfully!`,
            type: 'success'
          })
        );
      } else {
        throw new Error('Failed to update currency status');
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to update currency status. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setToggleLoading(null);
    }
  };

  // ---------- Handle Dialog Success ----------
  const handleDialogSuccess = () => {
    setEditDialogOpen(false);
    setRefreshKey((prev) => prev + 1);
    dispatch(
      showAlert({
        message: selectedCurrency ? 'Currency updated successfully!' : 'Currency created successfully!',
        type: 'success'
      })
    );
  };

  // ---------- Data Grid Columns ----------
  const columns: PaginatedDataGridColumn[] = [
    {
      field: 'name',
      headerName: 'Currency',
      flex: 1.5,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CurrencyExchangeIcon color="primary" fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'code',
      headerName: 'Code',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontWeight: 'medium', fontFamily: 'monospace' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {params.value || (
            <span style={{ fontStyle: 'italic' }}>
              No description provided
            </span>
          )}
        </Typography>
      )
    },
    {
      field: 'exchangeRate',
      headerName: 'Exchange Rate',
      width: 140,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'medium' }}>
          {params.value ? Number(params.value).toLocaleString() : '0'}
        </Typography>
      )
    },
    {
      field: 'isDefault',
      headerName: 'Default',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Default' : 'Regular'}
          color={params.value ? 'primary' : 'default'}
          size="small"
          variant={params.value ? 'filled' : 'outlined'}
        />
      )
    },
    {
      field: 'active',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={params.row.active ? 'Deactivate' : 'Activate'}>
            <IconButton
              size="small"
              onClick={() => handleToggleActive(params.row)}
              disabled={toggleLoading === params.row.id}
              color={params.row.active ? 'success' : 'default'}
            >
              {params.row.active ? <ToggleOnIcon /> : <ToggleOffIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditClick(params.row)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(params.row.id)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  // ---------- Data Grid Actions (Toolbar) ----------
  const actions: PaginatedDataGridAction[] = [
    {
      key: 'add',
      title: 'Add Currency',
      icon: <AddIcon />,
      onClick: handleAddCurrency,
      disabled: () => false,
      color: 'primary'
    }
  ];

  // ---------- Fetch Currencies Function ----------
  const fetchCurrencies = useCallback(
    async (params: {
      page: number;
      pageSize: number;
      sortModel?: any;
      filterModel?: any;
      search?: string;
    }) => {
      console.log('fetchCurrencies called with params:', params);
      try {
        const response = await DataService.get('api/Currency/all');

        if (response.ok) {
          const responseData = await response.json();
          console.log('API Response:', responseData);

          let rawCurrencies: any[] = [];

          if (responseData.success && responseData.data && Array.isArray(responseData.data)) {
            rawCurrencies = responseData.data;
          } else if (Array.isArray(responseData)) {
            rawCurrencies = responseData;
          } else {
            console.error('Unexpected response structure:', responseData);
            throw new Error('Invalid response structure');
          }

          const currencies: Currency[] = rawCurrencies.map(currency => ({
            id: currency.id,
            name: currency.name,
            description: currency.description,
            code: currency.code,
            exchangeRate: currency.exchangeRate || 0,
            isDefault: currency.isDefault || false,
            active: currency.active
          }));

          console.log('Raw currencies from API:', rawCurrencies);
          console.log('Mapped currencies:', currencies);

          // Filter by search term if provided
          let filteredCurrencies = currencies;
          if (params.search) {
            const searchLower = params.search.toLowerCase();
            filteredCurrencies = currencies.filter(
              currency =>
                currency.name.toLowerCase().includes(searchLower) ||
                currency.code.toLowerCase().includes(searchLower) ||
                (currency.description && currency.description.toLowerCase().includes(searchLower))
            );
          }

          // Apply sorting if provided
          if (params.sortModel && params.sortModel.length > 0) {
            const sortModel = params.sortModel[0];
            filteredCurrencies.sort((a, b) => {
              let aValue: any = a[sortModel.field as keyof Currency];
              let bValue: any = b[sortModel.field as keyof Currency];

              // Handle string comparison
              if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = (bValue as string).toLowerCase();
              }

              if (sortModel.sort === 'desc') {
                return aValue < bValue ? 1 : -1;
              }
              return aValue > bValue ? 1 : -1;
            });
          }

          // Apply pagination (DataGrid uses 0-based page indexing)
          const startIndex = params.page * params.pageSize;
          const paginatedCurrencies = filteredCurrencies.slice(startIndex, startIndex + params.pageSize);

          const totalPages = Math.ceil(filteredCurrencies.length / params.pageSize);

          const result = {
            items: paginatedCurrencies,
            totalCount: filteredCurrencies.length,
            pageNumber: params.page + 1,
            pageSize: params.pageSize,
            totalPages: totalPages,
            hasNext: params.page + 1 < totalPages,
            hasPrevious: params.page > 0
          };

          console.log('Final result being returned:', result);
          return result;
        } else {
          throw new Error(`Failed to fetch currencies: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        console.error('Error fetching currencies:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load currencies. Please try again.';
        dispatch(
          showAlert({
            message: errorMessage,
            type: 'error'
          })
        );
        return {
          items: [],
          totalCount: 0,
          pageNumber: params.page + 1,
          pageSize: params.pageSize,
          totalPages: 0,
          hasNext: false,
          hasPrevious: false
        };
      }
    },
    [dispatch]
  );

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 'bold' }}>
        Currencies Management
      </Typography>

      <PaginatedDataGrid
        key={refreshKey}
        title="Currencies"
        columns={columns}
        useBackendPagination
        onDataFetch={fetchCurrencies}
        actions={actions}
        emptyMessage="No currencies found"
      />

      {/* Delete Confirmation Dialog */}
      <SimpleConfirmationDialog
        open={!!deleteId}
        title="Delete Currency"
        message="Are you sure you want to delete this currency? This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onClose={handleDeleteCancel}
      />

      {/* Add/Edit Currency Dialog */}
      <CurrencyDialog
        open={editDialogOpen}
        currency={selectedCurrency}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={handleDialogSuccess}
      />
    </Box>
  );
};

export default CurrenciesListing;